import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { patientService } from '../services/dataSource';
import { Patient, UpdatePatientData } from '../types/patient';
import { useAuth } from '../hooks/useAuth';
import { useOrganizationStore } from '../stores/organizationStore';
import { toast } from '../utils/toast';
import { PhoneInput, EmailInput, DateInput, GenderSelect, LanguageSelect, EmergencyContactInput, TagInput, type GenderValue } from '../components/forms';
import { EmergencyContact } from '../types/patient';
import { patientMrnLabel, patientName } from '../utils/patientDisplay';
import {
  UNREADABLE_CONTACT_TEXT,
  contactForSave,
  contactFormValues,
  emergencyContactsForForm,
  emergencyContactsForSave,
} from '../utils/patientContact';

const EditPatient: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { operationMode } = useOrganizationStore();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [validationState, setValidationState] = useState({
    email: true,
    phone: true,
    dateOfBirth: true,
    emergencyContacts: true,
  });
  const [formData, setFormData] = useState<UpdatePatientData>({
    firstName: '',
    lastName: '',
    demographics: {
      dateOfBirth: '',
      gender: undefined,
      language: 'en'
    },
    contact: {
      phoneHash: '',
      emailHash: ''
    },
    emergencyContacts: [],
    medicalRecord: {
      mrn: '',
      allergies: [],
      conditions: [],
      medications: []
    }
  });

  // Only standalone admins and operators edit patients; anyone else is sent back to the list.
  const blockedReason = operationMode === 'connected'
    ? 'Cannot edit patients in connected mode'
    : user?.role === 'admin' || user?.role === 'operator'
    ? null
    : 'You do not have permission to edit patients';

  useEffect(() => {
    if (blockedReason) {
      toast.error(blockedReason);
      navigate('/patients', { replace: true });
    }
  }, [blockedReason, navigate]);

  useEffect(() => {
    if (id && !blockedReason) {
      fetchPatient();
    }
  }, [id, blockedReason]);

  const fetchPatient = async () => {
    try {
      setFetchLoading(true);
      const patientData = await patientService.getPatient(id!);
      setPatient(patientData);
      
      // Populate form with existing data
      setFormData({
        firstName: patientData.firstName || '',
        lastName: patientData.lastName || '',
        demographics: {
          dateOfBirth: patientData.demographics.dateOfBirth || '',
          gender: patientData.demographics.gender,
          language: patientData.demographics.language || 'en'
        },
        contact: contactFormValues(patientData),
        emergencyContacts: emergencyContactsForForm(patientData.emergencyContacts),
        medicalRecord: {
          mrn: patientData.medicalRecord.mrn || '',
          allergies: patientData.medicalRecord.allergies || [],
          conditions: patientData.medicalRecord.conditions || [],
          medications: patientData.medicalRecord.medications || []
        }
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch patient';
      toast.error(errorMessage);
      navigate('/patients');
    } finally {
      setFetchLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);

      // Basic validation
      const firstName = formData.firstName?.trim() || '';
      const lastName = formData.lastName?.trim() || '';
      if (!firstName || !lastName) {
        toast.error('First and last name are required');
        return;
      }

      if (!formData.demographics?.gender) {
        toast.error('Please select a gender');
        return;
      }

      // Check form validation state
      const hasValidationErrors = !validationState.email || !validationState.phone || !validationState.dateOfBirth || !validationState.emergencyContacts;
      if (hasValidationErrors) {
        toast.error('Please fix validation errors before submitting');
        return;
      }

      // Create update object - only include fields that have values
      const updateData: UpdatePatientData = { firstName, lastName };
      
      if (formData.demographics) {
        updateData.demographics = {};
        if (formData.demographics.dateOfBirth) {
          updateData.demographics.dateOfBirth = formData.demographics.dateOfBirth;
        }
        if (formData.demographics.gender) {
          updateData.demographics.gender = formData.demographics.gender;
        }
        if (formData.demographics.language) {
          updateData.demographics.language = formData.demographics.language;
        }
      }

      // Sends a changed or emptied phone/email; a blank value that was on file
      // but unreadable is left alone.
      const contact = contactForSave(formData.contact, patient?.contact);
      if (contact) {
        updateData.contact = contact;
      }

      // Always sent, so removing the last contact is saved too (contacts are optional, AMP-144).
      updateData.emergencyContacts = emergencyContactsForSave(formData.emergencyContacts, patient?.emergencyContacts);

      if (formData.medicalRecord) {
        updateData.medicalRecord = formData.medicalRecord;
      }

      // Update patient
      await patientService.updatePatient(id!, updateData);
      
      toast.success('Patient updated successfully');
      navigate(`/patients/${id}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update patient';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  const handleArchivePatient = async () => {
    if (!window.confirm('Are you sure you want to archive this patient? This will make them inactive but preserve their data.')) {
      return;
    }

    try {
      setLoading(true);
      await patientService.updatePatient(id!, { isActive: false });
      toast.success('Patient archived successfully');
      navigate('/patients');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to archive patient';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleReactivatePatient = async () => {
    try {
      setLoading(true);
      await patientService.updatePatient(id!, { isActive: true });
      toast.success('Patient reactivated successfully');
      // Refresh patient data
      await fetchPatient();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reactivate patient';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (blockedReason) return null;

  if (fetchLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-3 text-gray-600">Loading patient data...</span>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="card p-6 bg-red-50 border border-red-200">
        <div className="flex items-center">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-3" />
          <div>
            <p className="text-sm font-medium text-red-800">Patient not found</p>
            <button 
              onClick={() => navigate('/patients')} 
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Back to patients
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/patients/${id}`)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to Patient Details
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Patient</h1>
            <p className="mt-1 text-sm text-gray-600">
              {patientName(patient)} · {patientMrnLabel(patient)}
            </p>
          </div>
          <div className="flex space-x-3">
            {patient.isActive ? (
              <button
                onClick={handleArchivePatient}
                disabled={loading}
                className="btn-secondary bg-yellow-50 text-yellow-700 border-yellow-300 hover:bg-yellow-100"
              >
                Archive Patient
              </button>
            ) : (
              <button
                onClick={handleReactivatePatient}
                disabled={loading}
                className="btn-secondary bg-green-50 text-green-700 border-green-300 hover:bg-green-100"
              >
                Reactivate Patient
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Inactive Patient Warning */}
      {!patient.isActive && (
        <div className="mb-6 card p-4 bg-yellow-50 border border-yellow-200">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-3" />
            <div>
              <p className="text-sm text-yellow-800">
                <strong>Archived Patient:</strong> This patient is currently inactive. 
                You can reactivate them using the button above.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Patient Information Section */}
        <div className="card p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Patient Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="edit-patient-first-name" className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                id="edit-patient-first-name"
                type="text"
                className="input-field"
                maxLength={50}
                value={formData.firstName || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                required
              />
            </div>
            <div>
              <label htmlFor="edit-patient-last-name" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                id="edit-patient-last-name"
                type="text"
                className="input-field"
                maxLength={50}
                value={formData.lastName || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                required
              />
            </div>
          </div>
        </div>

        {/* Demographics Section */}
        <div className="card p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Demographics</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DateInput
              label="Date of Birth"
              value={formData.demographics?.dateOfBirth || ''}
              onChange={(value) => setFormData(prev => ({
                ...prev,
                demographics: {
                  ...prev.demographics!,
                  dateOfBirth: value
                }
              }))}
              onValidationChange={(isValid) => setValidationState(prev => ({ ...prev, dateOfBirth: isValid }))}
              showAge={true}
              maxDate={new Date().toISOString().split('T')[0]} // Today's date
              isDateOnly={true}
            />

            <GenderSelect
              label="Gender"
              value={formData.demographics?.gender || ''}
              onChange={(value: GenderValue) => setFormData(prev => ({
                ...prev,
                demographics: {
                  ...prev.demographics!,
                  gender: value
                }
              }))}
              required={true}
              showIcons={true}
            />

            <LanguageSelect
              label="Preferred Language"
              value={formData.demographics?.language || ''}
              onChange={(value) => setFormData(prev => ({
                ...prev,
                demographics: {
                  ...prev.demographics!,
                  language: value
                }
              }))}
              placeholder="Select preferred language"
              className="md:col-span-2"
            />
          </div>
        </div>

        {/* Contact Section */}
        <div className="card p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h2>
          <p className="text-sm text-gray-600 mb-4">
            Phone and email are stored encrypted. Clear a field to remove it.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PhoneInput
              label="Phone Number"
              value={formData.contact?.phoneHash || ''}
              onChange={(value) => setFormData(prev => ({
                ...prev,
                contact: {
                  ...prev.contact!,
                  phoneHash: value
                }
              }))}
              onValidationChange={(isValid) => setValidationState(prev => ({ ...prev, phone: isValid }))}
              placeholder={patient?.contact?.phoneUnreadable ? UNREADABLE_CONTACT_TEXT : undefined}
            />

            <EmailInput
              label="Email Address"
              value={formData.contact?.emailHash || ''}
              onChange={(value) => setFormData(prev => ({
                ...prev,
                contact: {
                  ...prev.contact!,
                  emailHash: value
                }
              }))}
              onValidationChange={(isValid) => setValidationState(prev => ({ ...prev, email: isValid }))}
              placeholder={patient?.contact?.emailUnreadable ? UNREADABLE_CONTACT_TEXT : undefined}
            />
          </div>
          {(patient?.contact?.phoneUnreadable || patient?.contact?.emailUnreadable) && (
            <p className="text-xs text-gray-500 mt-2">
              A value saved before encryption is on file but can't be shown. Re-enter it to view it here, or leave the field blank to keep it.
            </p>
          )}
        </div>

        {/* Emergency Contacts Section */}
        <div className="card p-6">
          <EmergencyContactInput
            value={formData.emergencyContacts || []}
            onChange={(contacts: EmergencyContact[]) => setFormData(prev => ({
              ...prev,
              emergencyContacts: contacts
            }))}
            onValidationChange={(isValid) => setValidationState(prev => ({ ...prev, emergencyContacts: isValid }))}
            required={false}
            minContacts={1}
            maxContacts={5}
          />
        </div>

        {/* Medical Record Section */}
        <div className="card p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Medical Information</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medical Record Number (MRN)
              </label>
              <input
                type="text"
                className="input-field"
                placeholder="MRN-12345"
                value={formData.medicalRecord?.mrn || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  medicalRecord: {
                    ...prev.medicalRecord!,
                    mrn: e.target.value
                  }
                }))}
              />
            </div>

            <TagInput
              label="Allergies"
              value={formData.medicalRecord?.allergies || []}
              onChange={(tags) => setFormData(prev => ({
                ...prev,
                medicalRecord: {
                  ...prev.medicalRecord!,
                  allergies: tags
                }
              }))}
              placeholder="Type allergy and press Enter"
              suggestions={[
                'Penicillin',
                'Aspirin',
                'Ibuprofen',
                'Shellfish',
                'Peanuts',
                'Tree nuts',
                'Eggs',
                'Milk',
                'Soy',
                'Wheat',
                'Fish',
                'Latex',
                'Sulfa drugs'
              ]}
              helperText="Common allergies will appear as suggestions"
              maxTags={50}
            />

            <TagInput
              label="Medical Conditions"
              value={formData.medicalRecord?.conditions || []}
              onChange={(tags) => setFormData(prev => ({
                ...prev,
                medicalRecord: {
                  ...prev.medicalRecord!,
                  conditions: tags
                }
              }))}
              placeholder="Type condition and press Enter"
              suggestions={[
                'Hypertension',
                'Diabetes Type 2',
                'Diabetes Type 1',
                'Asthma',
                'COPD',
                'Heart Disease',
                'Arthritis',
                'Osteoporosis',
                'Depression',
                'Anxiety',
                'Hypothyroidism',
                'Hyperthyroidism',
                'High Cholesterol'
              ]}
              helperText="Common conditions will appear as suggestions"
              maxTags={50}
            />

            <TagInput
              label="Current Medications"
              value={formData.medicalRecord?.medications || []}
              onChange={(tags) => setFormData(prev => ({
                ...prev,
                medicalRecord: {
                  ...prev.medicalRecord!,
                  medications: tags
                }
              }))}
              placeholder="Type medication and press Enter"
              suggestions={[
                'Metformin',
                'Lisinopril',
                'Atorvastatin',
                'Levothyroxine',
                'Amlodipine',
                'Metoprolol',
                'Omeprazole',
                'Simvastatin',
                'Losartan',
                'Gabapentin',
                'Hydrochlorothiazide',
                'Sertraline',
                'Aspirin',
                'Insulin'
              ]}
              helperText="Common medications will appear as suggestions"
              maxTags={100}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate(`/patients/${id}`)}
            className="btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>

      {/* HIPAA Notice */}
      <div className="mt-6 card p-4 bg-blue-50 border border-blue-200">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-800">
              <strong>Security Notice:</strong> All patient data is encrypted at rest and in transit. 
              Contact information is hashed for additional privacy protection. This action will be logged for audit purposes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPatient;